/**
 * Tenant Features Page
 * TASK-428: Per-tenant UI feature flags for the meeting cockpit feature registry.
 *
 * Lets an admin toggle which cockpit features (Ask AI panel, Portfolio /
 * Proposals tabs, …) are enabled for the account. Flags are stored on
 * `accounts.features` and ride the login JWT, so a change takes effect on the
 * tenant's next login / token refresh.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Save, ToggleRight } from 'lucide-react';
import { featuresService } from '../../services/admin';
import { logger } from '../../utils/logger';
import { FEATURE_TOGGLE_OPTIONS } from '../../types/admin';
import type { TenantFeatures } from '../../types/admin';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../utils/apiErrorHandler';

/** Project the raw flag map onto the known toggle keys (missing key === off). */
const toFormState = (features: TenantFeatures): Record<string, boolean> => {
  const state: Record<string, boolean> = {};
  for (const opt of FEATURE_TOGGLE_OPTIONS) {
    state[opt.key] = features[opt.key] === true;
  }
  return state;
};

const FeaturesPage: React.FC = () => {
  const [original, setOriginal] = useState<Record<string, boolean> | null>(null);
  const [form, setForm] = useState<Record<string, boolean> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const features = await featuresService.getFeatures();
      setOriginal(toFormState(features));
      setForm(toFormState(features));
    } catch (error) {
      logger.error('Failed to load tenant features:', error);
      toast.error(extractErrorMessage(error, 'Failed to load tenant features'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading || !form || !original) {
    return <div className="p-6 text-gray-500">Loading feature configuration…</div>;
  }

  const hasChanges = FEATURE_TOGGLE_OPTIONS.some(
    (opt) => form[opt.key] !== original[opt.key],
  );

  const handleToggle = (key: string) => {
    setForm((prev) => (prev ? { ...prev, [key]: !prev[key] } : prev));
  };

  const handleSave = async () => {
    setConfirmOpen(false);
    setIsSaving(true);
    try {
      // Send only the changed keys (PUT merges into the existing map).
      const changes: TenantFeatures = {};
      for (const opt of FEATURE_TOGGLE_OPTIONS) {
        if (form[opt.key] !== original[opt.key]) changes[opt.key] = form[opt.key];
      }
      const updated = await featuresService.updateFeatures(changes);
      setOriginal(toFormState(updated));
      setForm(toFormState(updated));
      toast.success('Feature flags updated. Changes apply on the next login.');
    } catch (error) {
      logger.error('Failed to update tenant features:', error);
      toast.error(extractErrorMessage(error, 'Failed to update tenant features'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center gap-2 mb-1">
        <ToggleRight className="w-5 h-5 text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-900">Cockpit Features</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Enable or disable meeting-cockpit features for this tenant. Changes take
        effect on the next login.
      </p>

      <div className="space-y-3">
        {FEATURE_TOGGLE_OPTIONS.map((opt) => (
          <label
            key={opt.key}
            className="flex items-start justify-between gap-4 p-4 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-gray-300"
          >
            <div>
              <div className="text-sm font-medium text-gray-900">{opt.label}</div>
              <div className="text-xs text-gray-500 mt-0.5">{opt.description}</div>
            </div>
            <input
              type="checkbox"
              checked={form[opt.key]}
              onChange={() => handleToggle(opt.key)}
              className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </label>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!hasChanges || isSaving}
        >
          <Save className="w-4 h-4 mr-2" />
          {isSaving ? 'Saving…' : 'Save changes'}
        </Button>
        {hasChanges && (
          <span className="text-xs text-amber-600">Unsaved changes</span>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Update cockpit features?"
        description="These feature flags apply to every advisor on this tenant. Changes take effect on each user's next login."
        confirmText="Save"
        onConfirm={handleSave}
      />
    </div>
  );
};

export default FeaturesPage;
